/**
 * RulesTab — manage procedural rules from the Memory dashboard.
 *
 * Two sub-tables stacked:
 *   - Proposed (top, sorted by evidence_count desc, with Promote / Edit&Promote / Retire)
 *   - Active   (below, sorted by promoted_at desc — already that way from the API)
 *
 * Promotion uses a confirm dialog that shows the full rule text and the
 * evidence IDs (clickable through to the investigation detail pages
 * already implemented elsewhere). Edit&Promote opens the same dialog in
 * editable mode so the admin can polish wording before promoting.
 */

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ApiError, memory } from '@/helpers/ApiHelper';
import { RULE_DOMAIN_LABELS, type ProceduralRuleOut, type RuleDomain } from '@/types/memory';

const TRUNCATE_AT = 80;
const DOMAIN_FILTERS: RuleDomain[] = ['investigation', 'review', 'pm_report'];

interface PromoteState {
  rule: ProceduralRuleOut;
  editable: boolean;
  draft: string;
}

interface RetireState {
  rule: ProceduralRuleOut;
}

export interface RulesTabProps {
  /** When false, all write actions (Promote / Edit&Promote / Retire) are hidden.
   * The backend enforces the same gate via the admin dependency — this flag
   * is just so non-admins don't see dead buttons. */
  canEdit: boolean;
}

export function RulesTab({ canEdit }: RulesTabProps): ReactElement {
  const [domain, setDomain] = useState<RuleDomain | 'all'>('all');
  const [rules, setRules] = useState<ProceduralRuleOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promote, setPromote] = useState<PromoteState | null>(null);
  const [retire, setRetire] = useState<RetireState | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  const refresh = useCallback(async () => {
    try {
      const data = await memory.listRules({
        status: ['proposed', 'active'],
        ...(domain === 'all' ? {} : { domain }),
      });
      setRules(data.results);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load rules');
    }
  }, [domain]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const proposed = (rules ?? [])
    .filter((r) => r.status === 'proposed')
    .sort((a, b) => b.evidence_count - a.evidence_count);
  const active = (rules ?? [])
    .filter((r) => r.status === 'active')
    .sort((a, b) => {
      const at = a.promoted_at ? Date.parse(a.promoted_at) : 0;
      const bt = b.promoted_at ? Date.parse(b.promoted_at) : 0;
      return bt - at;
    });

  const onConfirmPromote = useCallback(async () => {
    if (!promote) return;
    setBusy(true);
    try {
      await memory.promoteRule(promote.rule.id, {
        rule_text: promote.editable ? promote.draft : null,
      });
      setPromote(null);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Promote failed');
    } finally {
      setBusy(false);
    }
  }, [promote, refresh]);

  const onConfirmRetire = useCallback(async () => {
    if (!retire) return;
    setBusy(true);
    try {
      await memory.retireRule(retire.rule.id);
      setRetire(null);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Retire failed');
    } finally {
      setBusy(false);
    }
  }, [retire, refresh]);

  if (error) {
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );
  }
  if (rules == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading…
      </Typography>
    );
  }

  return (
    <Stack spacing={4}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={domain}
        onChange={(_, v: RuleDomain | 'all' | null) => {
          if (v !== null) setDomain(v);
        }}
        aria-label="Rule domain"
      >
        <ToggleButton value="all" sx={{ minHeight: 44 }}>
          All
        </ToggleButton>
        {DOMAIN_FILTERS.map((d) => (
          <ToggleButton key={d} value={d} sx={{ minHeight: 44 }}>
            {RULE_DOMAIN_LABELS[d]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <RuleSection
        title="Proposed"
        emptyHint="No proposed rules. The weekly synthesis job runs Mondays at 09:00 UTC."
        rules={proposed}
        onPromote={(rule) =>
          setPromote({ rule, editable: false, draft: rule.rule_text })
        }
        onEditAndPromote={(rule) =>
          setPromote({ rule, editable: true, draft: rule.rule_text })
        }
        onRetire={(rule) => setRetire({ rule })}
        showPromoteActions={canEdit}
        showRetireAction={canEdit}
      />
      <RuleSection
        title="Active"
        emptyHint="No active rules yet — promote one from the Proposed list above."
        rules={active}
        onPromote={() => undefined /* not shown */}
        onEditAndPromote={() => undefined /* not shown */}
        onRetire={(rule) => setRetire({ rule })}
        showPromoteActions={false}
        showRetireAction={canEdit}
      />

      {promote && (
        <PromoteDialog
          state={promote}
          busy={busy}
          onClose={() => setPromote(null)}
          onChangeDraft={(draft) => setPromote((s) => (s ? { ...s, draft } : s))}
          onConfirm={onConfirmPromote}
        />
      )}
      {retire && (
        <RetireDialog
          state={retire}
          busy={busy}
          onClose={() => setRetire(null)}
          onConfirm={onConfirmRetire}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Sub-table
// ---------------------------------------------------------------------------

interface RuleSectionProps {
  title: string;
  emptyHint: string;
  rules: ProceduralRuleOut[];
  showPromoteActions: boolean;
  showRetireAction: boolean;
  onPromote: (rule: ProceduralRuleOut) => void;
  onEditAndPromote: (rule: ProceduralRuleOut) => void;
  onRetire: (rule: ProceduralRuleOut) => void;
}

function RuleSection({
  title,
  emptyHint,
  rules,
  showPromoteActions,
  showRetireAction,
  onPromote,
  onEditAndPromote,
  onRetire,
}: RuleSectionProps): ReactElement {
  const showActionsColumn = showPromoteActions || showRetireAction;
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant="h6">{title}</Typography>
        <Chip label={rules.length} size="small" variant="outlined" />
      </Stack>
      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {emptyHint}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label={`${title} rules`}>
            <TableHead>
              <TableRow>
                <TableCell>Trigger</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell align="right">Evidence</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Domain</TableCell>
                {showActionsColumn && (
                  <TableCell align="right">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  showPromoteActions={showPromoteActions}
                  showRetireAction={showRetireAction}
                  onPromote={onPromote}
                  onEditAndPromote={onEditAndPromote}
                  onRetire={onRetire}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

interface RuleRowProps {
  rule: ProceduralRuleOut;
  showPromoteActions: boolean;
  showRetireAction: boolean;
  onPromote: (rule: ProceduralRuleOut) => void;
  onEditAndPromote: (rule: ProceduralRuleOut) => void;
  onRetire: (rule: ProceduralRuleOut) => void;
}

function RuleRow({
  rule,
  showPromoteActions,
  showRetireAction,
  onPromote,
  onEditAndPromote,
  onRetire,
}: RuleRowProps): ReactElement {
  const [expanded, setExpanded] = useState<boolean>(false);
  const truncated = rule.rule_text.length > TRUNCATE_AT;
  const display = expanded || !truncated ? rule.rule_text : `${rule.rule_text.slice(0, TRUNCATE_AT)}…`;

  return (
    <TableRow hover>
      <TableCell sx={{ verticalAlign: 'top', maxWidth: 240 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {rule.trigger_summary}
        </Typography>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Typography
          variant="body2"
          component="span"
          sx={{ cursor: truncated ? 'pointer' : 'default' }}
          onClick={() => truncated && setExpanded((v) => !v)}
        >
          {display}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
        <Tooltip title={`${rule.evidence_count} supporting investigation(s)`}>
          <Chip label={rule.evidence_count} size="small" variant="outlined" />
        </Tooltip>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Chip
          label={rule.status}
          size="small"
          color={rule.status === 'active' ? 'success' : 'default'}
          variant={rule.status === 'active' ? 'filled' : 'outlined'}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Chip size="small" variant="outlined" label={RULE_DOMAIN_LABELS[rule.domain]} />
      </TableCell>
      {(showPromoteActions || showRetireAction) && (
        <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
          {showPromoteActions && (
            <>
              <Tooltip title="Promote as-is">
                <IconButton
                  aria-label={`Promote rule ${rule.trigger_summary}`}
                  size="small"
                  onClick={() => onPromote(rule)}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit and promote">
                <IconButton
                  aria-label={`Edit and promote rule ${rule.trigger_summary}`}
                  size="small"
                  onClick={() => onEditAndPromote(rule)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {showRetireAction && (
            <Tooltip title={rule.status === 'active' ? 'Retire' : 'Discard'}>
              <IconButton
                aria-label={`Retire rule ${rule.trigger_summary}`}
                size="small"
                color="error"
                onClick={() => onRetire(rule)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

interface PromoteDialogProps {
  state: PromoteState;
  busy: boolean;
  onClose: () => void;
  onChangeDraft: (draft: string) => void;
  onConfirm: () => void;
}

function PromoteDialog({
  state,
  busy,
  onClose,
  onChangeDraft,
  onConfirm,
}: PromoteDialogProps): ReactElement {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {state.editable ? 'Edit and promote rule' : 'Promote rule'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="baseline">
            <Typography variant="overline" color="text.secondary">
              Trigger
            </Typography>
            <Typography variant="body2">{state.rule.trigger_summary}</Typography>
          </Stack>
          {state.editable ? (
            <TextField
              autoFocus
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
              label="Rule text"
              value={state.draft}
              onChange={(e) => onChangeDraft(e.target.value)}
              inputProps={{ maxLength: 4000 }}
            />
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {state.rule.rule_text}
            </Typography>
          )}
          {state.rule.evidence_ids.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Evidence
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {state.rule.evidence_ids.map((eid) => (
                  <Link
                    key={eid}
                    component={RouterLink}
                    to={`/investigations/${eid}`}
                    underline="hover"
                    variant="caption"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {eid.slice(0, 8)}
                  </Link>
                ))}
              </Stack>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">
            Promoting will inject this rule into the agentic system prompt&apos;s
            <code> ## Learned heuristics </code> section within one investigation
            (cache invalidation is forced on promote).
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy || (state.editable && state.draft.trim().length === 0)}
        >
          {state.editable ? 'Promote edited' : 'Promote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface RetireDialogProps {
  state: RetireState;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function RetireDialog({ state, busy, onClose, onConfirm }: RetireDialogProps): ReactElement {
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Retire rule</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            Retiring removes the rule from the agentic prompt. The row is kept
            for audit, not deleted.
          </Typography>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Trigger
            </Typography>
            <Typography variant="body2">{state.rule.trigger_summary}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Rule
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {state.rule.rule_text}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={busy}>
          Retire
        </Button>
      </DialogActions>
    </Dialog>
  );
}
